import api from './api';
import { PurchaseOrder, SupplierPayment, SupplierDebtSummary } from '@/types';
import { Unit } from '@/utils/units';

export interface PurchaseOrderItemCreate {
  product_id?: number;
  custom_name?: string;
  quantity_ordered: number;     // En quantity_unit
  quantity_unit?: Unit;         // Unidad en la que se compra
  unit_cost: number;            // $ por quantity_unit (backend convierte a base)
  discount_percentage?: number; // % de descuento por item (0-100)
  discount_amount?: number;     // monto fijo de descuento
  notes?: string;
}

export interface PurchaseOrderCreate {
  supplier_id?: number;
  order_type: 'standard' | 'debt_only';
  order_date?: string;
  description?: string;
  notes?: string;
  total_amount?: number;
  iva_included?: boolean;       // ¿el unit_cost ya incluye IVA?
  iva_percent?: number;         // % de IVA (0 si no se aplica)
  // Remito: el stock ya fue ingresado → la compra no suma stock
  stock_already_loaded?: boolean;
  remito_number?: string;
  remito_date?: string;
  items: PurchaseOrderItemCreate[];
}

export interface PurchaseOrderUpdate {
  supplier_id?: number;
  order_date?: string;
  description?: string;
  notes?: string;
  status?: string;
}

export interface PurchaseOrderFilters {
  supplier_id?: number;
  status?: string;
  skip?: number;
  limit?: number;
}

export interface ReceiveItemData {
  item_id: number;
  quantity_received: number;
}

export interface ReceiveRequest {
  items: ReceiveItemData[];
  notes?: string;
}

export interface SupplierPaymentCreate {
  amount: number;
  payment_method: string;
  payment_date?: string;
  reference?: string;
  notes?: string;
  deduct_from_register?: boolean;
}

export const purchaseOrdersService = {
  async getPurchaseOrders(filters?: PurchaseOrderFilters): Promise<PurchaseOrder[]> {
    const response = await api.get<PurchaseOrder[]>('/purchase-orders', { params: filters });
    return response.data;
  },

  async getPurchaseOrder(id: number): Promise<PurchaseOrder> {
    const response = await api.get<PurchaseOrder>(`/purchase-orders/${id}`);
    return response.data;
  },

  async createPurchaseOrder(data: PurchaseOrderCreate): Promise<PurchaseOrder> {
    const response = await api.post<PurchaseOrder>('/purchase-orders', data);
    return response.data;
  },

  async updatePurchaseOrder(id: number, data: PurchaseOrderUpdate): Promise<PurchaseOrder> {
    const response = await api.put<PurchaseOrder>(`/purchase-orders/${id}`, data);
    return response.data;
  },

  // Edita items/IVA de una orden estándar antes de recibir mercadería
  async editPurchaseOrder(id: number, data: PurchaseOrderCreate): Promise<PurchaseOrder> {
    const response = await api.put<PurchaseOrder>(`/purchase-orders/${id}/edit`, data);
    return response.data;
  },

  async cancelPurchaseOrder(id: number): Promise<PurchaseOrder> {
    const response = await api.post<PurchaseOrder>(`/purchase-orders/${id}/cancel`);
    return response.data;
  },

  async deletePurchaseOrder(id: number): Promise<void> {
    await api.delete(`/purchase-orders/${id}`);
  },

  async receiveItems(poId: number, data: ReceiveRequest): Promise<PurchaseOrder> {
    const response = await api.post<PurchaseOrder>(`/purchase-orders/${poId}/receive`, data);
    return response.data;
  },

  async registerSupplierPayment(supplierId: number, data: SupplierPaymentCreate): Promise<SupplierPayment> {
    const response = await api.post<SupplierPayment>(`/purchase-orders/supplier/${supplierId}/pay`, data);
    return response.data;
  },

  async getSupplierPayments(supplierId: number): Promise<SupplierPayment[]> {
    const response = await api.get<SupplierPayment[]>(`/purchase-orders/supplier/${supplierId}/payments`);
    return response.data;
  },

  async adjustSupplierDebt(
    supplierId: number,
    data: { amount: number; direction: 'reduce' | 'increase'; reason: string },
  ): Promise<{ pending: string }> {
    const response = await api.post(`/purchase-orders/supplier/${supplierId}/adjust-debt`, data);
    return response.data;
  },

  async updateSupplierPayment(
    paymentId: number,
    data: {
      amount?: number;
      payment_method?: string;
      payment_date?: string;
      reference?: string | null;
      notes?: string | null;
    },
  ): Promise<SupplierPayment> {
    const response = await api.put<SupplierPayment>(`/purchase-orders/payments/${paymentId}`, data);
    return response.data;
  },

  async deleteSupplierPayment(paymentId: number): Promise<void> {
    await api.delete(`/purchase-orders/payments/${paymentId}`);
  },

  async getDebtSummary(): Promise<SupplierDebtSummary[]> {
    const response = await api.get<SupplierDebtSummary[]>('/purchase-orders/debt-summary');
    return response.data;
  },
};
